import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { isKnownBrokenHero } from "../lib/heroImage";
import { Search, ArrowRight, Loader2, Calendar } from "lucide-react";
import { Input } from "../components/ui/input";

const SITE_URL = "https://instabridgefunding.com";

function formatDateLong(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

export default function BlogIndex() {
  const [data, setData] = useState({ posts: [], total: 0, categories: [] });
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useSEO(
    "Blog — pre-settlement funding insights for plaintiffs & attorneys",
    "Guides, FAQs, and case-study deep dives on pre-settlement legal funding, plaintiff finance, and the legal industry.",
    {
      canonical: `${SITE_URL}/blog`,
      ogType: "website",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Instabridge Funding Blog",
        url: `${SITE_URL}/blog`,
        publisher: { "@type": "Organization", name: "Instabridge Funding", url: SITE_URL },
      },
    }
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams({ limit: "300" });
    if (q.trim()) params.set("q", q.trim());
    if (activeCat && activeCat !== "All") params.set("category", activeCat);
    api.get(`/blog?${params.toString()}`)
      .then((res) => { if (active) setData(res.data); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [q, activeCat]);

  const [featured, ...rest] = data.posts;
  const cats = useMemo(() => ["All", ...(data.categories || [])], [data.categories]);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-index">
      <Navbar />

      {/* Hero strip */}
      <section className="border-b border-[#E5E7EB] bg-gradient-to-b from-[#F9FAFB] to-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20">
          <div className="uppercase-label mb-5 text-[#0066FF]">Resources & guides</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0A2540] font-semibold tracking-tight max-w-3xl">
            Built for plaintiffs. Written by people who fund them.
          </h1>
          <p className="mt-5 text-lg text-[#374151] max-w-2xl">
            Plain-English answers on pre-settlement funding, case strategy, and the law — {data.total} articles and counting.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles…"
                className="pl-11 h-12 rounded-full bg-white border-[#E5E7EB] text-base"
                data-testid="blog-search-input"
              />
            </div>
          </div>

          {cats.length > 1 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="blog-category-chips">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  data-testid={`blog-chip-${c.toLowerCase()}`}
                  className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                    activeCat === c
                      ? "bg-[#0A2540] text-white border border-[#0A2540]"
                      : "bg-white text-[#374151] border border-[#E5E7EB] hover:border-[#0A2540]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Posts grid */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-14 lg:py-20">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#6B7280]"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading articles…</div>
        ) : data.posts.length === 0 ? (
          <div className="text-center py-20" data-testid="blog-empty">
            <p className="text-[#6B7280]">No articles match that search yet. Try a different keyword or clear the filter.</p>
          </div>
        ) : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`} className="block mb-12 group" data-testid="blog-featured">
                <article className="grid lg:grid-cols-[1.3fr_1fr] gap-7 lg:gap-10 items-center">
                  {featured.hero_image && !isKnownBrokenHero(featured.hero_image) && (
                    <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-[#F3F4F6]">
                      <img
                        src={featured.hero_image}
                        alt={featured.hero_image_alt || featured.title}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        loading="eager"
                        onError={(e) => {
                          e.currentTarget.parentElement.classList.add("bg-gradient-to-br", "from-[#0A2540]", "to-[#001A3D]");
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-3 text-xs text-[#6B7280] mb-3">
                      <span className="uppercase tracking-wider font-medium text-[#0066FF]">{featured.category}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatDateLong(featured.published_at)}</span>
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold tracking-tight group-hover:text-[#0066FF] transition-colors">
                      {featured.title}
                    </h2>
                    {featured.excerpt && <p className="mt-4 text-[#374151] text-base leading-relaxed">{featured.excerpt}</p>}
                    <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#0066FF]">
                      Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </article>
              </Link>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-9" data-testid="blog-grid">
              {rest.map((p) => <PostCard key={p.slug} post={p} />)}
            </div>

            <div className="mt-12 pt-8 border-t border-[#E5E7EB] text-sm text-[#6B7280] text-center" data-testid="blog-count-footer">
              Showing {data.posts.length} of {data.total} articles
            </div>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}

function PostCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="group block" data-testid={`blog-card-${post.slug}`}>
      <article>
        {post.hero_image ? (
          <div className="aspect-[16/10] rounded-xl overflow-hidden bg-[#F3F4F6] mb-5">
            <img src={post.hero_image} alt={post.hero_image_alt || post.title}
                 loading="lazy"
                 className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          </div>
        ) : (
          <div className="aspect-[16/10] rounded-xl bg-gradient-to-br from-[#0A2540] to-[#001A3D] mb-5 flex items-center justify-center">
            <div className="font-display text-4xl text-white/30 px-6 text-center">{post.title.split(":")[0]}</div>
          </div>
        )}
        <div className="flex items-center gap-2 text-[11px] text-[#6B7280] uppercase tracking-wider mb-2">
          <span className="text-[#0066FF] font-medium">{post.category}</span>
          <span>·</span>
          <span>{formatDateLong(post.published_at)}</span>
        </div>
        <h3 className="font-display text-xl text-[#0A2540] font-semibold leading-tight group-hover:text-[#0066FF] transition-colors line-clamp-3">
          {post.title}
        </h3>
        {post.excerpt && <p className="mt-2.5 text-sm text-[#6B7280] leading-relaxed line-clamp-3">{post.excerpt}</p>}
      </article>
    </Link>
  );
}
